/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ITheme from "@atg/icf-react-components/themes/Theme";
import bidiCSSJS from "bidi-css-js";
import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";

import CustomLink from "~/components/CustomLink";
import { JSS_FONT_FAMILY_PRIMARY, JSS_MEDIA_MOBILE_MAX } from "~/constants";
import { selectAuthHasOneClick, selectAuthViewOnly } from "~/features/auth/selectors";
import HeaderNavLink from "~/features/header/components/HeaderNavLink";
import MenuItemWithSubmenu from "~/features/header/components/menucontent/MenuItemWithSubmenu";
import SubMenuItem from "~/features/header/components/SubMenuItem";
import { AuthenticatedMenuItem } from "~/features/header/constants";
import { useAuthenticatedMenuItems } from "~/features/header/utils/getMenuItems";

const useStyles = createUseStyles((theme: ITheme) => ({
  subMenuItem: {
    padding: 0,
    cursor: "pointer",
    background: "none",
    border: "none",
    boxShadow: "none",
    display: "block",
    color: theme.colors.baseContrast020,
    fontWeight: 300,
    width: "100%",
    ...bidiCSSJS({ textAlign: "start" }, theme.direction),
    "&:hover": {
      color: theme.colors.baseContrast020,
    },
    [JSS_MEDIA_MOBILE_MAX]: {
      color: theme.colors.baseContrast100,
      "&:hover": {
        color: theme.colors.baseContrast100,
      },
    },
  },
  button: {
    fontFamily: JSS_FONT_FAMILY_PRIMARY,
    fontWeight: 400,
    letterSpacing: -0.25,
  },
}));

const AuthenticatedMenuContent: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation("header");

  const hasOneClick = useSelector(selectAuthHasOneClick);
  const viewOnly = useSelector(selectAuthViewOnly);

  const menuItems = useAuthenticatedMenuItems({ viewOnly, hasOneClick, t });

  return (
    <>
      {menuItems[AuthenticatedMenuItem.DASHBOARD].available && (
        <li>
          <HeaderNavLink to={menuItems[AuthenticatedMenuItem.DASHBOARD].to}>
            {menuItems[AuthenticatedMenuItem.DASHBOARD].label}
          </HeaderNavLink>
        </li>
      )}
      {menuItems[AuthenticatedMenuItem.STATISTICS].available && (
        <li>
          <HeaderNavLink to={menuItems[AuthenticatedMenuItem.STATISTICS].to}>
            {menuItems[AuthenticatedMenuItem.STATISTICS].label}
          </HeaderNavLink>
        </li>
      )}
      {menuItems[AuthenticatedMenuItem.PROMO_TOOLS].available && (
        <li>
          <HeaderNavLink to={menuItems[AuthenticatedMenuItem.PROMO_TOOLS].to}>
            {menuItems[AuthenticatedMenuItem.PROMO_TOOLS].label}
          </HeaderNavLink>
        </li>
      )}
      {menuItems[AuthenticatedMenuItem.MY_SITES].available && (
        <li>
          <MenuItemWithSubmenu
            title={menuItems[AuthenticatedMenuItem.MY_SITES].label}
            navItemId="popover_menu_my_sites"
          >
            {menuItems[AuthenticatedMenuItem.MANAGE_SITES].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.MANAGE_SITES].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.MANAGE_SITES].to}>
                  {menuItems[AuthenticatedMenuItem.MANAGE_SITES].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].to}>
                  {menuItems[AuthenticatedMenuItem.CREATE_NEW_SITE].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.POSTBACKS].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.POSTBACKS].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.POSTBACKS].to}>
                  {menuItems[AuthenticatedMenuItem.POSTBACKS].label}
                </CustomLink>
              </SubMenuItem>
            )}
          </MenuItemWithSubmenu>
        </li>
      )}

      {menuItems[AuthenticatedMenuItem.SUPPORT].available && (
        <li>
          <MenuItemWithSubmenu title={menuItems[AuthenticatedMenuItem.SUPPORT].label} navItemId="popover_menu_support">
            {menuItems[AuthenticatedMenuItem.FAQ].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.FAQ].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.FAQ].to}>
                  {menuItems[AuthenticatedMenuItem.FAQ].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.API].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.API].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.API].to}>
                  {menuItems[AuthenticatedMenuItem.API].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.CONTACT].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.CONTACT].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.CONTACT].to}>
                  {menuItems[AuthenticatedMenuItem.CONTACT].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.ENCODE].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.ENCODE].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.ENCODE].to}>
                  {menuItems[AuthenticatedMenuItem.ENCODE].label}
                </CustomLink>
              </SubMenuItem>
            )}

            {menuItems[AuthenticatedMenuItem.DECODE].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.DECODE].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.DECODE].to}>
                  {menuItems[AuthenticatedMenuItem.DECODE].label}
                </CustomLink>
              </SubMenuItem>
            )}
          </MenuItemWithSubmenu>
        </li>
      )}

      {menuItems[AuthenticatedMenuItem.MY_ACCOUNT].available && (
        <li>
          <MenuItemWithSubmenu
            title={menuItems[AuthenticatedMenuItem.MY_ACCOUNT].label}
            navItemId="popover_menu_my_account"
          >
            {menuItems[AuthenticatedMenuItem.MY_DETAILS].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.MY_DETAILS].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.MY_DETAILS].to}>
                  {menuItems[AuthenticatedMenuItem.MY_DETAILS].label}
                </CustomLink>
              </SubMenuItem>
            )}
            {menuItems[AuthenticatedMenuItem.PERSONAL_INFORMATION].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.PERSONAL_INFORMATION].disabled}>
                <CustomLink
                  className={classes.subMenuItem}
                  to={menuItems[AuthenticatedMenuItem.PERSONAL_INFORMATION].to}
                >
                  {menuItems[AuthenticatedMenuItem.PERSONAL_INFORMATION].label}
                </CustomLink>
              </SubMenuItem>
            )}
            {menuItems[AuthenticatedMenuItem.DOCUMENTS].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.DOCUMENTS].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.DOCUMENTS].to}>
                  {menuItems[AuthenticatedMenuItem.DOCUMENTS].label}
                </CustomLink>
              </SubMenuItem>
            )}
            {menuItems[AuthenticatedMenuItem.PAYOUT_INFO].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.PAYOUT_INFO].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.PAYOUT_INFO].to}>
                  {menuItems[AuthenticatedMenuItem.PAYOUT_INFO].label}
                </CustomLink>
              </SubMenuItem>
            )}
            {menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].disabled}>
                <CustomLink className={classes.subMenuItem} to={menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].to}>
                  {menuItems[AuthenticatedMenuItem.EMAIL_PREFERENCES].label}
                </CustomLink>
              </SubMenuItem>
            )}
            {menuItems[AuthenticatedMenuItem.LOGOUT].available && (
              <SubMenuItem disabled={menuItems[AuthenticatedMenuItem.LOGOUT].disabled}>
                <CustomLink
                  className={clsx(classes.subMenuItem, classes.button)}
                  to={menuItems[AuthenticatedMenuItem.LOGOUT].to}
                >
                  {menuItems[AuthenticatedMenuItem.LOGOUT].label}
                </CustomLink>
              </SubMenuItem>
            )}
          </MenuItemWithSubmenu>
        </li>
      )}
    </>
  );
};

export default AuthenticatedMenuContent;
